import { useState, useCallback, useRef, useEffect } from 'react'
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard,
  Search,
  BedDouble,
  ClipboardList,
  AlertCircle,
  AlertTriangle,
  FileEdit,
  BookOpen,
  PackageOpen,
  Pill,
  Calendar,
  CalendarClock,
  Clock,
  DollarSign,
  FileText,
  CreditCard,
  Wallet,
  Receipt,
  BarChart3,
  FileBarChart,
  Activity,
  Users,
  Shield,
  Building2,
  UserCog,
  Plug,
  ScrollText,
  Settings,
  Key,
  Bell,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Sun,
  Moon,
  Laptop,
  Layers,
  Tag,
  Gauge,
  Grid3X3,
  ClipboardCheck,
  Stethoscope,
  UserCheck,
  Network,
  ShieldCheck,
  User,
  GitMerge,
  ExternalLink,
} from 'lucide-react'
import { useUIStore } from '../stores/uiStore'
import { useAuthStore } from '../stores/authStore'
import { useNotificationStore } from '../stores/notificationStore'
import { usePatientStore } from '../stores/patientStore'
import { useTheme } from './ThemeProvider'
import { useWebSocket } from '../hooks/useWebSocket'
import { useContextSync } from '../hooks/useContextSync'
import { api } from '../services/api'
import Breadcrumb from './Breadcrumb'
import DemoBanner, { DEMO_MODE } from './DemoBanner'
import LanguageSwitcher from './LanguageSwitcher'
import Logo from './Logo'
import { SessionTimeoutWarning } from './SessionTimeoutWarning'
import type { Patient } from '../types/api'
import { PERMS } from '../lib/permissions'

// --- Navigation Config ---

interface NavItem {
  label: string
  path: string
  icon: React.ElementType
  requiredPermission?: string
}

interface NavSection {
  label: string
  icon: React.ElementType
  items: NavItem[]
}

const navSections: NavSection[] = [
  {
    label: 'Clinical',
    icon: Activity,
    items: [
      { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard, requiredPermission: PERMS.PATIENT_VIEW },
      { label: 'Patient Search', path: '/patients/search', icon: Search, requiredPermission: PERMS.PATIENT_VIEW },
      { label: 'Census / Bed Board', path: '/encounters/census', icon: BedDouble, requiredPermission: PERMS.ENCOUNTER_VIEW },
      { label: 'Provider Directory', path: '/providers', icon: Stethoscope, requiredPermission: PERMS.PATIENT_VIEW },
      { label: 'Provider Coverage', path: '/coverage', icon: UserCheck, requiredPermission: PERMS.COVERAGE_GRANT },
      { label: 'Patient Merge', path: '/patients/merge', icon: GitMerge, requiredPermission: PERMS.PATIENT_MERGE },
    ],
  },
  {
    label: 'Worklists',
    icon: ClipboardList,
    items: [
      { label: 'Pending Results', path: '/results/pending', icon: ClipboardList, requiredPermission: PERMS.RESULT_VIEW },
      { label: 'Critical Results', path: '/results/critical', icon: AlertTriangle, requiredPermission: PERMS.RESULT_VIEW },
      { label: 'Unsigned Notes', path: '/notes/worklist', icon: FileEdit, requiredPermission: PERMS.DOC_VIEW },
      { label: 'Cosign Queue', path: '/orders/cosign-queue', icon: ClipboardCheck, requiredPermission: PERMS.ORDER_VIEW },
    ],
  },
  {
    label: 'Documentation',
    icon: ScrollText,
    items: [
      { label: 'Note Templates', path: '/notes/templates', icon: FileText, requiredPermission: PERMS.DOC_VIEW },
      { label: 'Smart Phrases', path: '/notes/smart-phrases', icon: ScrollText, requiredPermission: PERMS.DOC_VIEW },
    ],
  },
  {
    label: 'Orders',
    icon: BookOpen,
    items: [
      { label: 'Order Catalog', path: '/orders/catalog', icon: BookOpen, requiredPermission: PERMS.ORDER_VIEW },
      { label: 'Order Sets', path: '/orders/sets', icon: PackageOpen, requiredPermission: PERMS.ORDER_VIEW },
    ],
  },
  {
    label: 'Medications',
    icon: Pill,
    items: [
      { label: 'Formulary', path: '/medications/formulary', icon: Pill, requiredPermission: PERMS.MED_VIEW },
      { label: 'Pharmacist Queue', path: '/medications/pharmacist-queue', icon: Shield, requiredPermission: PERMS.MED_VERIFY },
    ],
  },
  {
    label: 'Scheduling',
    icon: Calendar,
    items: [
      { label: 'Appointments', path: '/appointments', icon: Calendar, requiredPermission: PERMS.SCHEDULE_VIEW },
      { label: 'Schedule Management', path: '/appointments/schedule', icon: CalendarClock, requiredPermission: PERMS.SCHEDULE_MANAGE },
      { label: 'Waitlist', path: '/appointments/waitlist', icon: Clock, requiredPermission: PERMS.SCHEDULE_VIEW },
    ],
  },
  {
    label: 'Billing',
    icon: DollarSign,
    items: [
      { label: 'Charges', path: '/billing/charges', icon: DollarSign, requiredPermission: PERMS.BILLING_VIEW },
      { label: 'Claims', path: '/billing/claims', icon: FileText, requiredPermission: PERMS.BILLING_VIEW },
      { label: 'Payments', path: '/billing/payments', icon: CreditCard, requiredPermission: PERMS.BILLING_VIEW },
      { label: 'Accounts', path: '/billing/accounts', icon: Wallet, requiredPermission: PERMS.BILLING_VIEW },
      { label: 'Statements', path: '/billing/statements', icon: Receipt, requiredPermission: PERMS.BILLING_VIEW },
      { label: 'Denials', path: '/billing/denials', icon: AlertCircle, requiredPermission: PERMS.BILLING_VIEW },
    ],
  },
  {
    label: 'Analytics',
    icon: BarChart3,
    items: [
      { label: 'Dashboard', path: '/analytics/dashboard', icon: BarChart3, requiredPermission: PERMS.ANALYTICS_VIEW },
      { label: 'Reports', path: '/analytics/reports', icon: FileBarChart, requiredPermission: PERMS.ANALYTICS_VIEW },
      { label: 'Quality Measures', path: '/analytics/quality', icon: Activity, requiredPermission: PERMS.ANALYTICS_VIEW },
    ],
  },
  // The old "Admin" section ran 11 items deep — bigger than any
  // other group and forced every sub-topic (users, facilities,
  // integrations, audit, CDS) through the same flat accordion.
  // Split into four focused sub-groups so each opens to 2–3 items
  // the operator can scan at a glance.
  {
    label: 'Organization',
    icon: Building2,
    items: [
      { label: 'Health Systems', path: '/admin/health-systems', icon: Building2, requiredPermission: PERMS.SYS_ADMIN_ALL },
      { label: 'Facilities', path: '/admin/facilities', icon: Building2, requiredPermission: PERMS.SYS_ADMIN_ALL },
      { label: 'Locations', path: '/admin/locations', icon: BedDouble, requiredPermission: PERMS.SYS_ADMIN_ALL },
      { label: 'Clinical Taxonomy', path: '/admin/taxonomy', icon: Stethoscope, requiredPermission: PERMS.SYS_ADMIN_ALL },
      { label: 'Service ↔ Location', path: '/admin/service-locations', icon: Network, requiredPermission: PERMS.SYS_ADMIN_ALL },
      { label: 'Providers', path: '/admin/providers', icon: UserCog, requiredPermission: PERMS.USER_MANAGE },
    ],
  },
  {
    label: 'Security',
    icon: Shield,
    items: [
      { label: 'Users', path: '/admin/users', icon: Users, requiredPermission: PERMS.USER_MANAGE },
      { label: 'Roles', path: '/admin/roles', icon: Shield, requiredPermission: PERMS.USER_MANAGE },
      { label: 'Access Matrix', path: '/admin/access-matrix', icon: Grid3X3, requiredPermission: PERMS.USER_MANAGE },
    ],
  },
  {
    label: 'Integrations',
    icon: Plug,
    items: [
      { label: 'Integrations', path: '/admin/integrations', icon: Plug, requiredPermission: PERMS.SYS_ADMIN_ALL },
      { label: 'Third-Party Apps', path: '/admin/third-party-apps', icon: ExternalLink, requiredPermission: PERMS.SYS_ADMIN_ALL },
    ],
  },
  {
    label: 'System',
    icon: Settings,
    items: [
      { label: 'Audit', path: '/admin/audit', icon: ScrollText, requiredPermission: PERMS.AUDIT_VIEW },
      { label: 'System', path: '/admin/system', icon: Settings, requiredPermission: PERMS.SYS_ADMIN_ALL },
      { label: 'CDS Rules', path: '/admin/cds-rules', icon: ShieldCheck, requiredPermission: PERMS.SYS_ADMIN_ALL },
    ],
  },
  {
    label: 'Licensing',
    icon: Key,
    items: [
      { label: 'Tiers', path: '/admin/licensing/tiers', icon: Layers, requiredPermission: PERMS.LICENSING_MANAGE },
      { label: 'Pricing', path: '/admin/licensing/pricing', icon: Tag, requiredPermission: PERMS.LICENSING_MANAGE },
      { label: 'Facilities', path: '/admin/licensing/facilities', icon: Building2, requiredPermission: PERMS.LICENSING_MANAGE },
      { label: 'Usage', path: '/admin/licensing/usage', icon: Gauge, requiredPermission: PERMS.LICENSING_MANAGE },
    ],
  },
]

// --- Sidebar ---

function Sidebar({
  collapsed,
  mobileOpen,
  onMobileClose,
}: {
  collapsed: boolean
  mobileOpen: boolean
  onMobileClose: () => void
}) {
  const location = useLocation()
  const hasPermission = useAuthStore(s => s.hasPermission)

  // Filter sections based on user permissions
  const visibleSections = navSections
    .map(section => ({
      ...section,
      items: section.items.filter(item =>
        !item.requiredPermission || hasPermission(item.requiredPermission)
      ),
    }))
    .filter(section => section.items.length > 0)

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(() => {
    // Auto-expand the section that contains the current route
    const initial: Record<string, boolean> = {}
    for (const section of visibleSections) {
      if (section.items.some(item => location.pathname.startsWith(item.path))) {
        initial[section.label] = true
      }
    }
    return initial
  })
  const [hoveredSection, setHoveredSection] = useState<string | null>(null)
  const toggleSidebar = useUIStore(s => s.toggleSidebar)

  const toggleSection = useCallback((label: string) => {
    setExpandedSections(prev => ({ ...prev, [label]: !prev[label] }))
  }, [])

  const isActive = (path: string) => {
    if (path === '/dashboard') return location.pathname === '/dashboard'
    return location.pathname.startsWith(path)
  }

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Logo / App Name */}
      <div className="flex items-center gap-3 px-4 h-16 border-b border-slate-700/50 shrink-0">
        <Logo className="h-8 w-8 shrink-0" />
        {!collapsed && (
          <span className="text-white font-semibold text-lg tracking-tight truncate">
            VynaHealth
          </span>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-1 scrollbar-thin" role="navigation" aria-label="Main navigation">
        {visibleSections.map(section => {
          const SectionIcon = section.icon
          const isExpanded = expandedSections[section.label] ?? false
          const hasActiveItem = section.items.some(item => isActive(item.path))
          const isFlyoutOpen = collapsed && hoveredSection === section.label

          return (
            <div
              key={section.label}
              className="relative"
              onMouseEnter={() => collapsed ? setHoveredSection(section.label) : undefined}
              onMouseLeave={() => collapsed ? setHoveredSection(null) : undefined}
            >
              {/* Section header */}
              <button
                onClick={() => collapsed ? undefined : toggleSection(section.label)}
                title={collapsed ? section.label : undefined}
                className={`w-full flex items-center gap-2 px-2 py-2 rounded-md text-sm font-medium transition-colors ${
                  hasActiveItem
                    ? 'text-emerald-400'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
                } ${collapsed ? 'justify-center' : ''}`}
              >
                <SectionIcon className="h-4 w-4 shrink-0" />
                {!collapsed && (
                  <>
                    <span className="flex-1 text-left truncate">{section.label}</span>
                    <ChevronDown
                      className={`h-3.5 w-3.5 transition-transform ${
                        isExpanded ? 'rotate-0' : '-rotate-90'
                      }`}
                    />
                  </>
                )}
              </button>

              {/* Flyout menu for collapsed sidebar */}
              {isFlyoutOpen && (
                <div
                  className="absolute left-full top-0 ml-1 z-50 w-52 rounded-md border border-slate-700 bg-slate-900 shadow-xl py-1"
                  onMouseEnter={() => setHoveredSection(section.label)}
                  onMouseLeave={() => setHoveredSection(null)}
                >
                  <div className="px-3 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider border-b border-slate-700/50">
                    {section.label}
                  </div>
                  {section.items.map(item => {
                    const ItemIcon = item.icon
                    const active = isActive(item.path)

                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => {
                          setHoveredSection(null)
                          onMobileClose()
                        }}
                        className={`flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
                          active
                            ? 'bg-emerald-500/15 text-emerald-400 font-medium'
                            : 'text-slate-300 hover:text-white hover:bg-slate-800'
                        }`}
                      >
                        <ItemIcon className="h-4 w-4 shrink-0" />
                        <span className="truncate">{item.label}</span>
                      </Link>
                    )
                  })}
                </div>
              )}

              {/* Section items (expanded sidebar only) */}
              {!collapsed && isExpanded && (
                <div className="ml-2 space-y-0.5 mt-0.5">
                  {section.items.map(item => {
                    const ItemIcon = item.icon
                    const active = isActive(item.path)

                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={onMobileClose}
                        className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors ${
                          active
                            ? 'bg-emerald-500/15 text-emerald-400 font-medium'
                            : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
                        }`}
                      >
                        <ItemIcon className="h-4 w-4 shrink-0" />
                        <span className="truncate">{item.label}</span>
                      </Link>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </nav>

      {/* Version display */}
      {!collapsed && (
        <div className="text-xs text-slate-500 px-4 py-1 shrink-0">
          VynaHealth v{import.meta.env.VITE_APP_VERSION || '1.0.0'}
        </div>
      )}

      {/* Collapse toggle (desktop only) */}
      <div className="hidden lg:block border-t border-slate-700/50 p-2 shrink-0">
        <button
          onClick={toggleSidebar}
          className="w-full flex items-center justify-center gap-2 px-2 py-2 rounded-md text-slate-400 hover:text-slate-200 hover:bg-slate-800/50 text-sm transition-colors"
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <>
              <ChevronLeft className="h-4 w-4" />
              <span>Collapse</span>
            </>
          )}
        </button>
      </div>
    </div>
  )

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={onMobileClose}
        />
      )}

      {/* Mobile sidebar */}
      <aside
        className={`fixed top-0 ${DEMO_MODE ? 'bottom-7' : 'bottom-0'} left-0 z-50 w-64 bg-slate-900 dark:bg-slate-950 transform transition-transform duration-200 ease-in-out lg:hidden ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <button
          onClick={onMobileClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-white"
          aria-label="Close sidebar"
        >
          <X className="h-5 w-5" />
        </button>
        {sidebarContent}
      </aside>

      {/* Desktop sidebar — ${DEMO_MODE ? 'lg:bottom-7' : 'lg:bottom-0'} leaves room for the demo-mode
          footer strip so the sidebar footer controls don't get
          covered. In production builds where the banner renders
          nothing, the 28px gap is invisible since pb-7 on the
          outer wrapper already matches. */}
      <aside
        className={`hidden lg:flex lg:flex-col lg:fixed lg:top-0 ${DEMO_MODE ? 'lg:bottom-7' : 'lg:bottom-0'} bg-slate-900 dark:bg-slate-950 transition-all duration-200 ease-in-out z-30 ${
          collapsed ? 'w-16' : 'w-64'
        }`}
      >
        {sidebarContent}
      </aside>
    </>
  )
}

// --- Top Bar ---

function TopBar({ onMenuClick }: { onMenuClick: () => void }) {
  const navigate = useNavigate()
  const { theme, setTheme } = useTheme()
  const user = useAuthStore(s => s.user)
  const logout = useAuthStore(s => s.logout)
  const selectedFacilityId = useAuthStore(s => s.selectedFacilityId)
  const selectFacility = useAuthStore(s => s.selectFacility)
  const unreadCount = useNotificationStore(s => s.unreadCount)
  const recentPatients = usePatientStore(s => s.recentPatients)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<Patient[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const [searchFocused, setSearchFocused] = useState(false)
  const searchRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Click-outside detection to close the dropdown
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowDropdown(false)
        setSearchFocused(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)

    if (!value.trim()) {
      setSearchResults([])
      setShowDropdown(true)
      return
    }

    debounceRef.current = setTimeout(async () => {
      setIsSearching(true)
      try {
        const res = await api.patients.list({ q: value.trim(), perPage: 5 })
        setSearchResults(res.data?.data ?? [])
        setShowDropdown(true)
      } catch {
        setSearchResults([])
      } finally {
        setIsSearching(false)
      }
    }, 300)
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setShowDropdown(false)
    if (searchQuery.trim()) {
      navigate(`/patients/search?q=${encodeURIComponent(searchQuery.trim())}`)
      setSearchQuery('')
      setSearchResults([])
    } else {
      navigate('/patients/search')
    }
  }

  const handleSelectPatient = (patientId: number) => {
    setShowDropdown(false)
    setSearchQuery('')
    setSearchResults([])
    navigate(`/patients/${patientId}/demographics`)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setShowDropdown(false)
    }
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const cycleTheme = () => {
    const order: Array<'light' | 'dark' | 'system'> = ['light', 'dark', 'system']
    const idx = order.indexOf(theme as 'light' | 'dark' | 'system')
    setTheme(order[(idx + 1) % order.length])
  }

  const ThemeIcon = theme === 'dark' ? Moon : theme === 'light' ? Sun : Laptop

  const showRecentPatients = searchFocused && !searchQuery.trim() && recentPatients.length > 0
  const showSearchResults = searchQuery.trim() && showDropdown

  return (
    <header className="sticky top-0 z-20 flex items-center gap-3 h-16 px-4 bg-white dark:bg-card border-b border-border">
      {/* Mobile hamburger */}
      <button
        onClick={onMenuClick}
        className="lg:hidden p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
      >
        <Menu className="h-5 w-5" />
      </button>

      {/* Global patient search with typeahead */}
      <div ref={searchRef} className="relative flex-1 max-w-md">
        <form onSubmit={handleSearch}>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => handleSearchChange(e.target.value)}
              onFocus={() => { setSearchFocused(true); setShowDropdown(true) }}
              onKeyDown={handleKeyDown}
              placeholder="Search patients (MRN, name, ID)..."
              className="w-full pl-9 pr-4 py-2 text-sm rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-hidden focus:ring-2 focus:ring-ring"
            />
          </div>
        </form>

        {/* Typeahead dropdown */}
        {showDropdown && (showRecentPatients || showSearchResults) && (
          <div className="absolute top-full left-0 right-0 mt-1 z-50 rounded-lg border border-border bg-popover shadow-lg overflow-hidden">
            {/* Recent patients when input is empty */}
            {showRecentPatients && (
              <>
                <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider border-b">
                  Recent Patients
                </div>
                {recentPatients.slice(0, 5).map(p => (
                  <button
                    key={p.patientId}
                    onClick={() => handleSelectPatient(p.patientId)}
                    className="flex items-center gap-3 w-full px-3 py-2 text-left hover:bg-accent transition-colors"
                  >
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 text-muted-foreground shrink-0">
                      <User className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-foreground truncate">{p.fullName}</div>
                      <div className="text-xs text-muted-foreground">
                        {p.mrn} &middot; {p.gender} &middot; DOB {p.dateOfBirth}
                      </div>
                    </div>
                  </button>
                ))}
              </>
            )}

            {/* Search results */}
            {showSearchResults && (
              <>
                {isSearching ? (
                  <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                    Searching...
                  </div>
                ) : searchResults.length > 0 ? (
                  searchResults.map(p => (
                    <button
                      key={p.patientId}
                      onClick={() => handleSelectPatient(p.patientId)}
                      className="flex items-center gap-3 w-full px-3 py-2 text-left hover:bg-accent transition-colors"
                    >
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 text-muted-foreground shrink-0">
                        <User className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-foreground truncate">{p.fullName}</div>
                        <div className="text-xs text-muted-foreground">
                          {p.mrn} &middot; {p.genderLegal} &middot; DOB {p.dateOfBirth}
                        </div>
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                    No patients found. Press Enter to search.
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 ml-auto">
        {/* Notification bell */}
        <Link
          to="/notifications"
          className="relative p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
          title="Notifications"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Link>

        {/* Language switcher */}
        <LanguageSwitcher />

        {/* Theme toggle */}
        <button
          onClick={cycleTheme}
          className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
          title={`Theme: ${theme}`}
          aria-label="Toggle theme"
        >
          <ThemeIcon className="h-5 w-5" />
        </button>

        {/* Facility selector */}
        <select
          value={selectedFacilityId ?? ''}
          onChange={e => selectFacility(Number(e.target.value))}
          className="hidden sm:block text-sm rounded-md border border-input bg-background text-foreground px-2 py-1.5 focus:outline-hidden focus:ring-2 focus:ring-ring"
          title="Select facility"
        >
          <option value="">All Facilities</option>
          {(user?.facilityIds ?? []).map(id => (
            <option key={id} value={id}>
              Facility {id}
            </option>
          ))}
        </select>

        {/* User info */}
        {user && (
          <Link
            to="/profile"
            className="hidden md:flex items-center gap-2 px-2 text-sm rounded-md hover:bg-accent transition-colors"
            title="My Profile"
          >
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-400 font-semibold text-xs">
              {user.fullName
                .split(' ')
                .map(n => n[0])
                .join('')
                .slice(0, 2)
                .toUpperCase()}
            </div>
            <div className="leading-tight">
              <div className="font-medium text-foreground truncate max-w-[120px]">
                {user.fullName}
              </div>
              <div className="text-xs text-muted-foreground truncate max-w-[120px]">
                {user.roleNames?.[0] ?? 'User'}
              </div>
            </div>
          </Link>
        )}

        {/* Logout */}
        <button
          onClick={handleLogout}
          className="p-2 rounded-md text-muted-foreground hover:text-red-500 hover:bg-accent transition-colors"
          title="Logout"
          aria-label="Sign out"
        >
          <LogOut className="h-5 w-5" />
        </button>
      </div>
    </header>
  )
}

// --- AppShell ---

export default function AppShell() {
  const sidebarCollapsed = useUIStore(s => s.sidebarCollapsed)
  const sidebarOpen = useUIStore(s => s.sidebarOpen)
  const setSidebarOpen = useUIStore(s => s.setSidebarOpen)
  const accessToken = useAuthStore(s => s.accessToken)
  useWebSocket({ token: accessToken, enabled: !!accessToken })
  useContextSync()

  const toggleMobile = useCallback(() => {
    setSidebarOpen(!sidebarOpen)
  }, [sidebarOpen, setSidebarOpen])

  const closeMobile = useCallback(() => {
    setSidebarOpen(false)
  }, [setSidebarOpen])

  return (
    // pb-7 leaves room for the fixed DemoBanner strip at the bottom
    // of the viewport in demo builds; stripped from production bundles
    // (Vite inlines DEMO_MODE at build time).
    <div className={`min-h-screen bg-background ${DEMO_MODE ? 'pb-7' : ''}`}>
      <SessionTimeoutWarning />
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-[60] focus:top-2 focus:left-2 focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md"
      >
        Skip to content
      </a>

      <Sidebar
        collapsed={sidebarCollapsed}
        mobileOpen={sidebarOpen}
        onMobileClose={closeMobile}
      />

      {/* Main area offset by sidebar width */}
      <div
        className={`transition-all duration-200 ease-in-out ${
          sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
        }`}
      >
        <TopBar onMenuClick={toggleMobile} />

        <main id="main-content" role="main" className="p-4 lg:p-6">
          <div className="mb-4">
            <Breadcrumb />
          </div>
          <Outlet />
        </main>
      </div>

      {/* Demo / PHI compliance strip (fixed at bottom, only rendered
          when VITE_DEMO_MODE=true). Sits outside the sidebar-offset
          wrapper so it spans the full viewport width. */}
      <DemoBanner />
    </div>
  )
}
